import { Injectable, NgModuleFactory, Type, Compiler, NgModuleRef, NgModuleFactoryLoader, Injector } from '@angular/core';
import { Observable, of, from, isObservable, throwError } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

export interface ModuleLoaderDef {
  name: string,
  load: () => Promise<NgModuleFactory<any>|Type<any>|any>
}

export type ModuleRegistryType =
    NgModuleRef<any>
  | { name: string, path: string }
  | ModuleLoaderDef
  | string;

@Injectable({
  providedIn: 'root'
})
export class ModuleLoaderService {

  private _registry = new Map<string, ModuleLoaderDef>();
  private _cache = new Map<string, any>();

  constructor(
    private injector: Injector,
    private compiler: Compiler,
    private moduleLoader: NgModuleFactoryLoader) { }

  public register(moduleLoaderDefs: ModuleLoaderDef[]): void {
    moduleLoaderDefs.forEach(value => {
      this._registry.set(value.name, value);
    });
  }

  public getOrLoad(moduleDef: ModuleRegistryType, localInjector?: Injector): Observable<NgModuleRef<any>> {

    if (typeof moduleDef !== 'string' && (<NgModuleRef<any>>moduleDef).componentFactoryResolver != null) {
      return of(moduleDef as NgModuleRef<any>);
    }

    const name = this.getName(moduleDef);
    const moduleReference = this._cache.get(name);

    if (moduleReference != null) {
      return isObservable(moduleReference)
        ? moduleReference
        : of(moduleReference);
    }

    let result: Observable<any>;

    if (typeof moduleDef === 'string') {
      const registryValue = this._registry.get(moduleDef);
      if (registryValue == null) {
        return throwError(`No NgModule module loader specified for '${moduleDef}'`);
      }

      result = from(registryValue.load())
        .pipe(
          tap(_ => console.log('NgModule loaded', name)),
          map(moduleOrFactory => {
            if (moduleOrFactory instanceof NgModuleFactory) {
              return moduleOrFactory;
            }
            return this.compiler.compileModuleAsync(moduleOrFactory);
          }),
          map((factory: NgModuleFactory<any>) => {
            const ngModuleRef = factory.create(localInjector || this.injector);
            this._cache.set(name, ngModuleRef);
            console.log('ngModule created', name);
            return ngModuleRef;
          }),
          shareReplay(1)
        );
    } else {
      const pathAndModule = `${moduleDef['path']}#${moduleDef['name']}`;
      result = from(this.moduleLoader.load(pathAndModule))
        .pipe(
          map(factory => {
            const ngModuleRef = factory.create(localInjector || this.injector);
            this._cache.set(name, ngModuleRef);
            return ngModuleRef;
          }),
          shareReplay(1)
        );
    }

    this._cache.set(name, result);
    return result;
  }

  private getName(moduleDef: ModuleRegistryType): string {
    if (typeof moduleDef === 'string') {
      return moduleDef;
    } else {
      return moduleDef['name'];
    }
  }

}